// Password rules shared by sign-up and the account screen.
// Follows NCSC guidance: length over forced complexity, and block the passwords
// attackers try first. The server still enforces its own minimum.

export const PASSWORD_MIN = 10;

// The most-used passwords in breach lists (lower-cased, compared without spaces).
const COMMON = new Set([
  'password', 'password1', 'password12', 'password123', 'password1234', 'passw0rd', 'p@ssw0rd', 'p@ssword',
  '123456', '1234567', '12345678', '123456789', '1234567890', '0123456789', '987654321', '111111', '000000',
  'qwerty', 'qwerty123', 'qwertyuiop', 'qwerty12345', '1q2w3e4r', '1q2w3e4r5t', 'asdfghjkl', 'zxcvbnm',
  'iloveyou', 'letmein', 'letmein123', 'welcome', 'welcome1', 'welcome123', 'monkey', 'dragon', 'football',
  'baseball', 'sunshine', 'princess', 'starwars', 'superman', 'trustno1', 'whatever', 'abc123', 'abcd1234',
  'admin', 'admin123', 'administrator', 'changeme', 'changeme123', 'default', 'secret', 'login', 'master',
  'cloudsafe', 'cloudsafe1', 'cloudsafe123', 'contractor', 'contractor1', 'safety', 'safety123',
  'liverpool', 'arsenal', 'chelsea', 'manchester', 'summer2024', 'summer2025', 'winter2025', 'spring2026',
]);

const hasSequence = (s) => /(.)\1{3,}/.test(s) || /(0123|1234|2345|3456|4567|5678|6789|abcd|qwer|asdf)/i.test(s);

/**
 * Check a password. Returns { ok, error, score (0-4), label }.
 * `context` is a list of personal strings (email, names) the password must not contain.
 */
export function checkPassword(pw, context = []) {
  const value = String(pw || '');
  const squashed = value.toLowerCase().replace(/\s+/g, '');
  let error = '';
  if (!value) error = 'Enter a password';
  else if (value.length < PASSWORD_MIN) error = `Use at least ${PASSWORD_MIN} characters`;
  else if (COMMON.has(squashed) || COMMON.has(squashed.replace(/[0-9!@#$%^&*]+$/, ''))) error = 'That password is too common — choose something less guessable';
  else {
    const personal = context
      .map((c) => String(c || '').toLowerCase().split('@')[0].trim())
      .filter((c) => c.length >= 4);
    if (personal.some((c) => squashed.includes(c))) error = "Don't use your name or email in your password";
  }

  // Strength score: length plus variety, minus obvious patterns.
  let score = 0;
  if (value.length >= PASSWORD_MIN) score += 1;
  if (value.length >= 14) score += 1;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(value)).length;
  if (classes >= 3) score += 1;
  if (value.length >= 18 || (value.length >= 12 && classes === 4)) score += 1;
  if (hasSequence(value)) score = Math.max(0, score - 1);
  if (error) score = Math.min(score, 1);
  const label = !value ? '' : ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][score];
  return { ok: !error, error, score, label };
}

export default checkPassword;
